import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '@/api/client'
import type {
  JobAssignee,
  JobAssignmentCandidate,
  JobSummary,
  UnassignedJob,
} from '@/api/types'
import { useAuth } from '@/auth/AuthContext'

/**
 * Who runs which job.
 *
 * `JobAssignment` was seeded once from Sage's Manager code and then had no
 * way to be maintained — the seed script or SQL, neither of which is something
 * to hand the office. This is that screen.
 *
 * THE UNASSIGNED QUEUE IS THE POINT, not the job list. Jobs appear in the
 * portal the moment they are created in Sage and a portal-owned assignment
 * does not follow, so a new job silently belongs to nobody. That failure is
 * invisible unless something counts it, which is why the queue leads and
 * carries a count that can reach zero.
 *
 * Sage's own Manager code rides along as a HINT and never as an answer: 'CS'
 * matches two employees and 'TL' matches three, which is exactly why the
 * original seed needed a human to resolve it.
 */

function dayLabel(iso: string | null): string {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-').map(Number)
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
               'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]
  return `${d} ${mon} ${y}`
}

export function JobAssignmentsPage() {
  const { user } = useAuth()
  const canEdit = user?.role === 'Admin'

  const [unassigned, setUnassigned] = useState<UnassignedJob[] | null>(null)
  const [candidates, setCandidates] = useState<JobAssignmentCandidate[]>([])
  const [jobs, setJobs] = useState<JobSummary[]>([])

  const [openJob, setOpenJob] = useState<string | null>(null)
  const [assignees, setAssignees] = useState<JobAssignee[]>([])
  const [picked, setPicked] = useState<Set<number>>(new Set())

  const [filter, setFilter] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [u, c, j] = await Promise.all([
        api.unassignedJobs(),
        api.assignmentCandidates(),
        api.jobs(),
      ])
      setUnassigned(u)
      setCandidates(c)
      setJobs(j)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load')
      setUnassigned([])
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  /** Open a job for editing and pull who is on it now. */
  async function open(jobNo: string) {
    if (openJob === jobNo) {
      setOpenJob(null)
      return
    }
    setOpenJob(jobNo)
    setAssignees([])
    setPicked(new Set())
    setNotice(null)
    try {
      const a = await api.jobAssignments(jobNo)
      setAssignees(a)
      setPicked(new Set(a.map((x) => x.employee_id)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load assignments')
    }
  }

  async function save(jobNo: string) {
    setBusy(true)
    setError(null)
    try {
      const a = await api.setJobAssignments(jobNo, [...picked])
      setAssignees(a)
      setNotice(
        a.length === 0
          ? `${jobNo} now has no project manager.`
          : `${jobNo} → ${a.map((x) => x.display_name).join(', ')}`,
      )
      // The queue changes shape on every save, so it is re-read rather than
      // patched in place — a stale count is the one thing this screen must not
      // show.
      setUnassigned(await api.unassignedJobs())
      setCandidates(await api.assignmentCandidates())
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Could not save. Nothing was changed.',
      )
    } finally {
      setBusy(false)
    }
  }

  const dirty = useMemo(() => {
    const now = new Set(assignees.map((a) => a.employee_id))
    if (now.size !== picked.size) return true
    for (const id of picked) if (!now.has(id)) return true
    return false
  }, [assignees, picked])

  const unassignedKeys = useMemo(
    () => new Set((unassigned ?? []).map((u) => u.job_no)),
    [unassigned],
  )

  const shownJobs = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return []
    return jobs
      .filter(
        (j) =>
          j.job_no.toLowerCase().includes(q) ||
          (j.job_description ?? '').toLowerCase().includes(q),
      )
      .slice(0, 25)
  }, [jobs, filter])

  function toggle(id: number) {
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  /** The editor, shared by both lists so a job reads the same either way. */
  function editor(jobNo: string, hint?: string | null) {
    if (openJob !== jobNo) return null
    return (
      <div className="card" style={{ borderColor: 'var(--c-brand)', marginTop: 8 }}>
        {hint && (
          <p className="muted" style={{ marginTop: 0, fontSize: '0.8125rem' }}>
            Sage's manager code for this job is <span className="mono">{hint}</span> —
            a hint, not an answer. Two-letter codes match more than one person.
          </p>
        )}

        {assignees.length > 0 && (
          <p className="muted" style={{ marginTop: 0, fontSize: '0.8125rem' }}>
            {assignees.some((a) => (a.source_note ?? '').startsWith('Seeded'))
              ? 'Assigned by the original seed and not reviewed since.'
              : 'Set by hand.'}
          </p>
        )}

        <div style={{ maxHeight: 320, overflowY: 'auto', marginBottom: 12 }}>
          {candidates.map((c) => {
            const on = picked.has(c.employee_id)
            return (
              <button
                key={c.employee_id}
                className={`btn ${on ? '' : 'btn-secondary'} btn-block`}
                style={{ justifyContent: 'flex-start', marginBottom: 6 }}
                aria-pressed={on}
                disabled={!canEdit}
                onClick={() => toggle(c.employee_id)}
              >
                <span style={{ width: '1.5rem' }}>{on ? '✓' : ''}</span>
                {c.display_name}
                <span
                  className="muted"
                  style={{ fontWeight: 400, marginLeft: 'auto', fontSize: '0.8125rem' }}
                >
                  {c.job_title ?? '—'}
                  {!c.is_portal_active && ' · cannot sign in'}
                  {c.current_job_count > 0 && ` · ${c.current_job_count} jobs`}
                </span>
              </button>
            )
          })}
        </div>

        {canEdit ? (
          <button
            className="btn btn-block"
            disabled={busy || !dirty}
            onClick={() => void save(jobNo)}
          >
            {busy
              ? 'Saving…'
              : !dirty
                ? 'No changes'
                : picked.size === 0
                  ? 'Remove everyone from this job'
                  : `Save ${picked.size} ${picked.size === 1 ? 'person' : 'people'}`}
          </button>
        ) : (
          <p className="muted" style={{ margin: 0, fontSize: '0.8125rem' }}>
            Only an Admin can change who runs a job.
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-ok">{notice}</div>}

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Job assignments</h2>
        <p className="muted" style={{ marginBottom: 0 }}>
          Who runs which job. Held by the portal, not by Sage — Sage's manager
          code is free text with no master list behind it, and two letters do not
          identify a person. A job may have more than one manager; five currently
          do.
        </p>
      </div>

      {/* The queue leads. A new job in Sage lands here and stays until someone
          deals with it, which is the only thing that makes the gap visible. */}
      <div className="card">
        <div className="crew-heading">
          <h2 style={{ margin: 0 }}>
            Unassigned{' '}
            {unassigned !== null && (
              <span className={unassigned.length ? 'badge badge-warn' : 'badge badge-ok'}>
                {unassigned.length}
              </span>
            )}
          </h2>
        </div>

        {unassigned === null ? (
          <p className="muted">Loading…</p>
        ) : unassigned.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            Every open job has a project manager.
          </p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Description</th>
                  <th>State</th>
                  <th>Est. start</th>
                  <th>Sage code</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {unassigned.map((u) => (
                  <Fragment key={u.job_no}>
                    <tr>
                      <td className="mono">{u.job_no}</td>
                      <td>{u.job_description}</td>
                      <td>{u.state_code ?? '—'}</td>
                      <td className="muted">{dayLabel(u.estimated_start_date)}</td>
                      <td>
                        {u.sage_manager_code ? (
                          <span className="mono" title="A hint from Sage, not an answer">
                            {u.sage_manager_code}
                          </span>
                        ) : (
                          <span className="muted">none</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => void open(u.job_no)}
                        >
                          {openJob === u.job_no ? 'Close' : 'Assign'}
                        </button>
                      </td>
                    </tr>
                    {openJob === u.job_no && (
                      <tr>
                        <td colSpan={6}>{editor(u.job_no, u.sage_manager_code)}</td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Changing an already-assigned job is the rarer errand, so it costs a
          search rather than occupying the screen with 318 rows. */}
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Change an assigned job</h2>
        <div className="form-field">
          <label htmlFor="jobFilter">Search</label>
          <input
            id="jobFilter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Job number or name"
            autoCapitalize="characters"
          />
        </div>
        {shownJobs.map((j) => (
          <div key={j.job_no}>
            <button
              className="btn btn-secondary btn-block"
              style={{ justifyContent: 'flex-start', marginBottom: 6 }}
              onClick={() => void open(j.job_no)}
            >
              <span className="mono">{j.job_no}</span>
              <span className="muted" style={{ fontWeight: 400 }}>
                {j.job_description}
              </span>
              {unassignedKeys.has(j.job_no) && (
                <span className="badge badge-warn" style={{ marginLeft: 'auto' }}>
                  unassigned
                </span>
              )}
            </button>
            {editor(j.job_no)}
          </div>
        ))}
        {filter.trim() && shownJobs.length === 0 && (
          <p className="muted">No matching open jobs.</p>
        )}
      </div>
    </div>
  )
}
